import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import useAccount from '~/hooks/useAccount';
import useFilteredHostingSites from '~/hooks/useFilteredHostingSites';
import BtnGroup from '~/scenes/AddSetupSiteFlows/components/MultiStepCard/BtnGroup';
import MultiStepCardHeader from '~/scenes/AddSetupSiteFlows/components/MultiStepCard/CardHeader';

export type ReassignDomainConfirmationPropOptions = {
  domain: string;
  siteName?: string | null;
  siteSubtype?: string;
  loading?: boolean;
  isPendingAdd?: boolean;
  disabled?: boolean;
  showInstallationModal?: boolean;
  handleCloseInstallModal?: () => void;
  handleCancelBtn?: () => void;
  handleBackBtn?: () => void;
  handleProceedBtn?: () => void;
};

const ReassignDomainConfirmation = ({
  domain,
  siteName = undefined,
  siteSubtype = undefined,
  loading = false,
  isPendingAdd = false,
  disabled = false,
  showInstallationModal = false,
  handleCloseInstallModal = () => {},
  handleCancelBtn = () => {},
  handleBackBtn = undefined,
  handleProceedBtn = undefined,
}: ReassignDomainConfirmationPropOptions) => {
  const { t } = useTranslation('addSites');
  const { id: hostingId } = useAccount();

  const { data: sitesInfo } = useFilteredHostingSites(hostingId);
  const siteList = sitesInfo?.data?.items;
  const oldSite = useMemo(
    () =>
      siteList?.find(
        (site) => site?.domain?.toLowerCase() === domain?.toLowerCase(),
      ),
    [siteList, domain],
  );

  const showDetailedDesc = useMemo(
    () => !!domain && !!oldSite?.name && !!siteName,
    [domain, oldSite?.name, siteName],
  );

  return (
    <Stack spacing={2} data-testid={'reassign-domain-conf'}>
      <MultiStepCardHeader
        headerTitle={t('domainAlreadyTaken.title')}
        hasBackArrow={true}
        handleBack={handleBackBtn}
        headerVariant="h2"
      />
      <Typography>
        {showDetailedDesc
          ? t('domainAlreadyTaken.desc', {
              domain: domain,
              oldSite: oldSite?.name,
              newSite: siteName,
            })
          : t('domainAlreadyTaken.descGeneric')}
      </Typography>
      <BtnGroup
        hasSkipBtn={false}
        hasCancelBtn
        showInstallationModal={showInstallationModal}
        btnColor="error"
        btnText={t('domainAlreadyTaken.proceedBtn')}
        loading={loading}
        disabled={disabled}
        domainChangeLoading={isPendingAdd}
        type={siteSubtype}
        onCancelHandler={handleCancelBtn}
        onCloseHandler={handleCloseInstallModal}
        {...(!!handleProceedBtn && { onClickHandler: handleProceedBtn })}
      />
    </Stack>
  );
};

export default ReassignDomainConfirmation;
